import mergeAllOfLib from "json-schema-merge-allof";
import type { JsonSchema, TransformResult, TransformOptions } from "./types";

/**
 * Check if schema has any oneOf/anyOf combinators (recursively)
 * mergeAllOf library can alter these, so we skip merging if present
 */
function hasCombinators(schema: any): boolean {
  if (!schema || typeof schema !== "object") return false;

  if (
    (Array.isArray(schema.oneOf) && schema.oneOf.length > 0) ||
    (Array.isArray(schema.anyOf) && schema.anyOf.length > 0)
  ) {
    return true;
  }

  for (const v of Object.values(schema)) {
    if (Array.isArray(v)) {
      for (const item of v) {
        if (hasCombinators(item)) return true;
      }
    } else if (hasCombinators(v)) {
      return true;
    }
  }

  return false;
}

/**
 * Step 2: Merge allOf compositions
 *
 * Flattens `allOf` combinators into a single schema object. Only runs if:
 * - Schema has properties (it's an object schema)
 * - Schema has NO oneOf/anyOf anywhere (preserves combinator renderers)
 *
 * @param schema - Dereferenced schema
 * @param options - Transform options
 * @returns Transformed schema and metadata
 *
 * @example
 * const input = {
 *   allOf: [
 *     { type: "object", properties: { name: { type: "string" } } },
 *     { properties: { age: { type: "integer" } } }
 *   ]
 * };
 * const result = mergeAllOf(input);
 * // result.schema = { type: "object", properties: { name: ..., age: ... } }
 */
export function mergeAllOf(
  schema: JsonSchema,
  options?: TransformOptions
): TransformResult {
  const { debug = false } = options || {};

  if (debug) {
    console.log("🔧 [mergeAllOf] Starting...");
  }

  // Check skip conditions
  const hasProperties = Object.keys(schema.properties || {}).length > 0;
  const hasCombinatorsPresent = hasCombinators(schema);

  if (!hasProperties || hasCombinatorsPresent) {
    if (debug) {
      const reason = !hasProperties
        ? "No properties found"
        : "Has oneOf/anyOf combinators";
      console.log(`⏭️  [mergeAllOf] Skipped: ${reason}`);
    }

    return {
      schema,
      metadata: {
        name: "mergeAllOf",
        transformedKeys: [],
        flags: {},
        skipped: true,
        reason: !hasProperties
          ? "No properties"
          : "Has oneOf/anyOf combinators",
      },
    };
  }

  try {
    const merged = mergeAllOfLib(schema);

    if (debug) {
      console.log("✅ [mergeAllOf] Complete");
    }

    return {
      schema: merged,
      metadata: {
        name: "mergeAllOf",
        transformedKeys: [], // mergeAllOf handles this internally
        flags: {},
        skipped: false,
      },
    };
  } catch (err: any) {
    if (debug) {
      console.error("❌ [mergeAllOf] Failed:", err);
    }
    throw new Error(err?.message || "Failed to merge schema");
  }
}
